import type { Routes } from '@angular/router';
import { DashboardViewPageComponent } from './dashboard/pages/view/dashboard-view-page.component';
import { authenticatedGuard } from '@lib/auth/authenticated.guard';
import { guestGuard } from '@lib/auth/guest.guard';
import { PackageViewPageComponent } from './packages/pages/view/package-view-page.component';
import { ProvidersIndexPageComponent } from './providers/pages/index/providers-index-page.component';
import { ClientsIndexPageComponent } from './clients/pages/index/clients-index-page.component';
import { ClientViewPageComponent } from './clients/pages/view/client-view-page.component';
import { ShellComponent } from './shell/shell.component';
import { ProvidersViewPageComponent } from './providers/pages/view/providers-view-page.component';
import { CommissionersIndexPageComponent } from './commissioners/pages/index/commissioners-index-page.component';
import { RegionsIndexPageComponent } from './regions/pages/index/regions-index-page.component';
import { UsersIndexPageComponent } from './users/pages/index/users-index-page.component';
import { CommissionersViewPageComponent } from './commissioners/pages/view/commissioners-view-page.component';
import { UsersViewPageComponent } from './users/pages/view/users-view-page.component';
import { SettingsGeneralInformationPageComponent } from './settings/general-information/pages/view/settings-general-information-page.component';
import { PackageAddPageComponent } from './packages/pages/add/package-add-page.component';
import { CommissionerAddPageComponent } from './commissioners/pages/add/commissioner-add-page.component';
import { SettingsCareTypesIndexPageComponent } from './settings/care-types/pages/index/settings-care-types-index-page.component';
import { SettingsCareOptionsIndexPageComponent } from './settings/care-options/pages/index/settings-care-options-index-page.component';
import { ClientsAddPageComponent } from './clients/pages/add/clients-add-page.component';
import { UsersAddPageComponent } from './users/pages/add/users-add-page.component';
import { ProvidersAddPageComponent } from './providers/pages/add/providers-add-page.component';
import { RegionsViewPageComponent } from './regions/pages/view/regions-view-page.component';
import { RegionsAddPageComponent } from './regions/pages/add/regions-add-page.component';
import { SettingHandbackReasonsIndexPageComponent } from './settings/handback-reasons/pages/index/setting-handback-reasons-index-page.component';
import { SettingPositionsIndexPageComponent } from './settings/positions/pages/index/setting-positions-index-page.component';
import { SettingsCareTypesAddPageComponent } from './settings/care-types/pages/add/settings-care-types-add-page.component';
import { SettingsCareOptionsAddPageComponent } from './settings/care-options/pages/add/settings-care-options-add-page.component';
import { MessagesIndexPageComponent } from './messages/pages/index/messages-index-page.component';
import { ReportsIndexPageComponent } from './reports/pages/index/reports-index-page.component';
import { ReportsViewPageComponent } from './reports/pages/view/reports-view-page.component';
import { MyPackagesComponent } from './packages/pages/my-packages/my-packages.component';
import { PotentialPackagesComponent } from './packages/pages/potential-packages/potential-packages.component';
import { SettingSystemAdminsIndexPageComponent } from './settings/system-admins/pages/index/setting-system-admins-index-page.component';
import { SettingsDataUploadIndexPageComponent } from './settings/data-upload/pages/index/settings-data-upload-index-page.component';
import { SettingsApiPageComponent } from './settings/api/pages/view/settings-api-page.component';

export const routes: Routes = [
    // Lazy load the auth module
    {
        path: 'auth',
        loadChildren: () => import('./auth/auth.module'),

        // Make sure it can only be accessed by unauthenticated users
        canActivate: [guestGuard]
    },

    {
        path: '',
        // Make sure the user is authenticated, or redirect them to the login page
        canActivate: [authenticatedGuard],

        component: ShellComponent,

        children: [
            {
                path: '',
                component: DashboardViewPageComponent,

                canActivate: [authenticatedGuard]
            },
            {
                path: 'my-packages',
                component: MyPackagesComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'potential-packages',
                component: PotentialPackagesComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'packages/:id',
                component: PackageViewPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'package-notes',
                component: PackageViewPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'package-notes/:id',
                component: PackageViewPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'add-package',
                component: PackageAddPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'clients',
                component: ClientsIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'clients/:id',
                component: ClientViewPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'add-client',
                component: ClientsAddPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'providers',
                component: ProvidersIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'providers/:id',
                component: ProvidersViewPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'add-provider',
                component: ProvidersAddPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'commissioners',
                component: CommissionersIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'commissioners/:id',
                component: CommissionersViewPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'add-commissioner',
                component: CommissionerAddPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'create-commissioner',
                component: CommissionerAddPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'messages',
                component: MessagesIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'regions',
                component: RegionsIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'regions/:id',
                component: RegionsViewPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'add-region',
                component: RegionsAddPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'users',
                component: UsersIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'users/:id',
                component: UsersViewPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'add-user',
                component: UsersAddPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'reports',
                component: ReportsIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'reports/:id',
                component: ReportsViewPageComponent,
                canActivate: [authenticatedGuard]

            },
            {
                path: 'settings/general-information',
                component: SettingsGeneralInformationPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'settings/care-types',
                component: SettingsCareTypesIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'settings/care-types/add',
                component: SettingsCareTypesAddPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'settings/care-options',
                component: SettingsCareOptionsIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'settings/care-options/add',
                component: SettingsCareOptionsAddPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'settings/handback-reasons',
                component: SettingHandbackReasonsIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'settings/positions',
                component: SettingPositionsIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'settings/system-admins',
                component: SettingSystemAdminsIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'settings/data-upload',
                component: SettingsDataUploadIndexPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: 'settings/api',
                component: SettingsApiPageComponent,
                canActivate: [authenticatedGuard]
            },
            {
                path: '**',
                redirectTo: '',
                pathMatch: 'full'
            }
        ]
    }
];
